import { useMutation, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import {
	type OpeningHours,
	type OpeningPeriod,
	openingHoursSchema,
	type RestaurantProfilePatch,
	WEEKDAYS,
	type Weekday,
} from "@luckymenu/core";
import { useCurrentMembership } from "./use-current";

/**
 * More > Restaurant profile, shared by both apps: the current restaurant and
 * a save that updates the cached copy. Needs `settings.manage`.
 */
export function useRestaurantProfile() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const current = useCurrentMembership();
	const save = useMutation(
		trpc.staff.tenancy.updateProfile.mutationOptions({
			onSuccess: (restaurant) => {
				queryClient.setQueryData(trpc.staff.tenancy.getCurrent.queryKey(), (old) =>
					old ? { ...old, restaurant } : old,
				);
				void queryClient.invalidateQueries({
					queryKey: trpc.staff.tenancy.listMyRestaurants.queryKey(),
				});
			},
		}),
	);
	return {
		restaurant: current.data?.restaurant ?? null,
		save: (patch: RestaurantProfilePatch) => save.mutateAsync(patch),
		saving: save.isPending,
		saveError: save.error,
		resetSave: () => save.reset(),
	};
}

/**
 * Opening hours as a form edits them: every weekday, closed or with one or
 * two periods. Times are "HH:MM", 24 hour, as `<input type="time">` gives.
 */
export type HoursDraft = Record<Weekday, { open: boolean; periods: OpeningPeriod[] }>;

const DEFAULT_PERIOD: OpeningPeriod = { open: "11:00", close: "22:00" };

export function hoursToDraft(hours: OpeningHours): HoursDraft {
	return Object.fromEntries(
		WEEKDAYS.map((day) => {
			const periods = hours[day] ?? [];
			return [
				day,
				{ open: periods.length > 0, periods: periods.length > 0 ? periods : [DEFAULT_PERIOD] },
			];
		}),
	) as HoursDraft;
}

/** The hours to save, or the first problem: `invalid` for a bad time or equal open and close. */
export function draftToHours(
	draft: HoursDraft,
): { ok: true; hours: OpeningHours } | { ok: false; day: Weekday } {
	const hours: OpeningHours = {};
	for (const day of WEEKDAYS) {
		const entry = draft[day];
		if (entry.open) hours[day] = entry.periods;
	}
	const parsed = openingHoursSchema.safeParse(hours);
	if (parsed.success) return { ok: true, hours: parsed.data };
	const day = parsed.error.issues[0]?.path[0] as Weekday | undefined;
	return { ok: false, day: day ?? "mon" };
}

/** "Same every day": copies one day to the other six. */
export function copyDayToAll(draft: HoursDraft, from: Weekday): HoursDraft {
	const source = draft[from];
	return Object.fromEntries(
		WEEKDAYS.map((day) => [
			day,
			{ open: source.open, periods: source.periods.map((p) => ({ ...p })) },
		]),
	) as HoursDraft;
}

export { DEFAULT_PERIOD };
